import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import ComplianceRequestDialog from '../ComplianceRequestDialog';

// Pre-start screens: "generating" waiting state + the candidate intro/start form.
export function AssessmentIntro({
  assessment, accessCode, candidateName, setCandidateName, candidateEmail, setCandidateEmail,
  onStart, onRefresh, showAccommodation, setShowAccommodation,
}) {
  if (!assessment.questions || assessment.questions.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#F1EAE1', padding: '2rem' }}>
        <Card style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: 14, padding: '2.5rem', maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <span className="status-dot status-processing" style={{ display: 'inline-block', width: 14, height: 14, marginBottom: '1rem' }} />
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, color: '#92400E', marginBottom: '0.5rem' }}>Assessment is being prepared...</h2>
          <p style={{ fontSize: '0.875rem', color: '#A16207', lineHeight: 1.6, marginBottom: '1rem' }}>AI is generating your questions and independently verifying every answer key. This usually takes 45-90 seconds. The page will refresh automatically.</p>
          <Button onClick={onRefresh} style={{ background: '#D97706', color: '#fff', borderRadius: 10, fontWeight: 600 }}>Refresh Now</Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#F1EAE1', padding: '2rem' }}>
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '2.5rem', maxWidth: 520, width: '100%' }}>
        <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>{assessment.title}</h2>
        <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>For: {assessment.job_title}</p>
        <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1.5rem', lineHeight: 1.6 }}>{assessment.description}</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Questions', value: assessment.questions.length },
            { label: 'Points', value: assessment.total_points },
            { label: 'Time Limit', value: `${assessment.time_limit_minutes} min` }
          ].map(({ label, value }) => (
            <div key={label} style={{ padding: '0.875rem', borderRadius: 10, background: '#F1EAE1', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, color: '#0076A3' }}>{value}</div>
              <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ padding: '0.875rem', borderRadius: 10, background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.15)', fontSize: '0.75rem', color: '#D97706', marginBottom: '0.875rem' }}>
          <strong>Anti-Cheating Enabled:</strong> Tab switches, copy/paste, and right-clicks are monitored and logged.
        </div>

        <div data-testid="aedt-assessment-notice" style={{ padding: '0.875rem', borderRadius: 10, background: 'rgba(30,104,255,0.06)', border: '1px solid rgba(30,104,255,0.15)', fontSize: '0.75rem', color: '#334155', marginBottom: '1.5rem', lineHeight: 1.6 }}>
          <strong style={{ color: '#0076A3' }}>AI is used to help evaluate this assessment</strong> (NYC Local Law 144). A recruiter reviews results — AI does not make the final decision.{' '}
          <a href="/ai-transparency" target="_blank" rel="noopener noreferrer" style={{ color: '#0076A3', fontWeight: 600 }}>How AI is used</a>.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.5rem' }}>
          <input
            data-testid="assessment-candidate-name"
            placeholder="Your full name" value={candidateName}
            onChange={(e) => setCandidateName(e.target.value)}
            style={{ width: '100%', padding: '0.75rem', borderRadius: 10, border: '1px solid #CBD5E1', background: '#F1EAE1', color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
          />
          <input
            data-testid="assessment-candidate-email"
            placeholder="Your email" type="email" value={candidateEmail}
            onChange={(e) => setCandidateEmail(e.target.value)}
            style={{ width: '100%', padding: '0.75rem', borderRadius: 10, border: '1px solid #CBD5E1', background: '#F1EAE1', color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        <Button data-testid="assessment-start-button" onClick={onStart} style={{ width: '100%', background: '#0076A3', color: '#fff', borderRadius: 999, padding: '0.875rem', fontWeight: 600, fontSize: '1rem' }}>
          Start Assessment
        </Button>

        <button
          type="button"
          data-testid="request-accommodation-button"
          onClick={() => setShowAccommodation(true)}
          style={{ display: 'block', width: '100%', marginTop: '0.875rem', background: 'none', border: 'none', color: '#0076A3', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
        >
          Need an accommodation? Request one (ADA)
        </button>
      </Card>

      <ComplianceRequestDialog
        open={showAccommodation}
        onOpenChange={setShowAccommodation}
        category="ada_accommodation"
        context="assessment"
        accessCode={accessCode}
        jobTitle={assessment.job_title}
        defaultName={candidateName}
        defaultEmail={candidateEmail}
      />
    </div>
  );
}
